import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Divider, Link as MuiLink, Paper, Stack, Typography } from '@mui/material';
import PageLayout from '../../components/layout/PageLayout';
import PageHeader from '../../components/layout/PageHeader';

/**
 * The terms a resident accepts at sign-up.
 *
 * Not conditions of sale: nothing is sold here, and what residents and
 * professionals agree between themselves is theirs. What the text has to carry
 * is what the Digital Services Act (art. 14) asks of any host of other
 * people's content — what is refused, how it is moderated, and how a decision
 * can be contested — and what the Code de la consommation (L111-7, L111-7-2)
 * asks of a directory: how listings are ordered, and whether reviews are
 * checked.
 *
 * UPDATED_ON moves with RegisterUserCommandHandler::TERMS_VERSION on the
 * backend: the version an account records is what proves which text it
 * accepted.
 */
const UPDATED_ON = '16 octobre 2026';

function Section({ title, children }) {
  return (
    <Box component="section">
      <Typography variant="h3" component="h2" sx={{ mb: 1.25 }}>
        {title}
      </Typography>
      <Stack spacing={1.25}>{children}</Stack>
    </Box>
  );
}

function Paragraph({ children }) {
  return <Typography variant="body1">{children}</Typography>;
}

function ContactLink() {
  return (
    <MuiLink component={RouterLink} to="/nous-ecrire">
      Nous écrire
    </MuiLink>
  );
}

function TermsOfUse() {
  return (
    <PageLayout centered={false}>
      <Box sx={{ width: '100%', maxWidth: 760 }}>
        <PageHeader
          title="Conditions d’utilisation"
          lede={`Ce que vous acceptez en créant un compte, et ce que vous pouvez attendre de nous. Dernière mise à jour : ${UPDATED_ON}.`}
        />

        <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 4 } }}>
          <Stack spacing={4} divider={<Divider flexItem />}>
            <Section title="Le service">
              <Paragraph>
                Vie de Quartier réunit, pour une ville, un annuaire des services et des commerces,
                un fil de discussion entre voisins et un agenda. Il est gratuit, sans publicité, et
                édité par un particulier à titre non professionnel (voir les{' '}
                <MuiLink component={RouterLink} to="/mentions-legales">
                  mentions légales
                </MuiLink>
                ).
              </Paragraph>
              <Paragraph>
                Tout le monde peut lire le site sans compte. Ces conditions s’appliquent à qui en
                crée un pour y participer ; les accepter est la condition de son ouverture.
              </Paragraph>
            </Section>

            <Section title="Votre compte">
              <Paragraph>
                Un compte correspond à une personne, âgée d’au moins 15 ans, et à une adresse email
                qui est la sienne : elle doit être confirmée avant la première connexion. Vous êtes
                responsable de ce qui est fait avec votre mot de passe ; gardez-le pour vous.
              </Paragraph>
              <Paragraph>
                Vous pouvez fermer votre compte à tout moment en le demandant par le formulaire{' '}
                <ContactLink />.
              </Paragraph>
            </Section>

            <Section title="Ce que vous publiez">
              <Paragraph>
                Vos publications, commentaires, événements, avis et fiches restent les vôtres. En
                les publiant, vous autorisez leur affichage sur le site, gratuitement et aussi
                longtemps qu’ils y restent. Vous en êtes responsable.
              </Paragraph>
              <Paragraph>
                Sont interdits : les contenus illégaux ; les propos haineux, injurieux ou
                discriminatoires ; le harcèlement ; la publication des coordonnées ou de la photo
                d’une personne sans son accord ; les arnaques et les annonces trompeuses ; les faux
                avis, y compris sur sa propre fiche ou celle d’un concurrent ; l’usurpation
                d’identité ; la publicité en dehors de l’annuaire et des offres des commerces.
              </Paragraph>
            </Section>

            <Section title="Comment le site est modéré">
              <Paragraph>
                Les fiches de l’annuaire sont lues par un modérateur avant d’apparaître, et
                modifier une fiche déjà en ligne la renvoie en relecture. Les publications du fil,
                les commentaires, les événements, les avis et les offres des commerces paraissent
                tout de suite, sans relecture préalable : ils sont modérés après coup, à la suite
                d’un signalement ou lorsqu’un modérateur les remarque.
              </Paragraph>
              <Paragraph>
                Chacun peut signaler un contenu qui lui semble contraire à ces règles, avec le
                bouton « Signaler » présent sur chacun d’eux. Chaque signalement est examiné par une
                personne : aucune décision n’est prise par un automate.
              </Paragraph>
              <Paragraph>
                Un contenu contraire aux règles est masqué ou refusé, et son auteur en reçoit le
                motif. Des manquements graves ou répétés peuvent conduire à la suspension du compte,
                elle aussi motivée : un compte suspendu ne peut plus se connecter, sans que rien
                de ce qu’il a publié ne soit supprimé pour autant.
              </Paragraph>
              <Paragraph>
                Vous pouvez contester une décision qui vous concerne par le formulaire{' '}
                <ContactLink /> : elle est alors réexaminée par une personne. Cette démarche ne vous
                prive pas du droit de saisir les tribunaux.
              </Paragraph>
            </Section>

            <Section title="Les professionnels et l’annuaire">
              <Paragraph>
                Un professionnel tient une seule fiche, qui reste invisible jusqu’à sa validation.
                Une entreprise doit indiquer sa raison sociale et son numéro SIRET, qui ne sont
                jamais affichés publiquement. Les informations de la fiche doivent être exactes et
                tenues à jour.
              </Paragraph>
              <Paragraph>
                Les fiches sont classées par ordre alphabétique, après les filtres choisis par le
                lecteur (catégorie, quartier, recherche). Aucune place ne s’achète : le site ne
                perçoit aucune rémunération des professionnels qui y figurent.
              </Paragraph>
              <Paragraph>
                Un commerce peut annoncer des offres : trois en même temps au plus, trente jours
                chacune. Elles sont publiées sans relecture préalable et peuvent être retirées si
                elles ne respectent pas ces règles.
              </Paragraph>
              <Paragraph>
                Une demande de rendez-vous n’engage personne tant que le professionnel ne l’a pas
                confirmée. Vie de Quartier met en relation, mais n’est partie à aucun accord entre
                un habitant et un professionnel : prix, prestation et paiement se règlent entre
                eux.
              </Paragraph>
            </Section>

            <Section title="Les avis">
              <Paragraph>
                Un habitant laisse au plus un avis par fiche, noté de 1 à 5, qu’il peut modifier.
                Les avis sont publiés tels qu’ils sont écrits, sans relecture préalable ; un avis
                signalé est examiné par un modérateur, qui le masque s’il ne respecte pas ces
                règles. Le site ne vérifie pas que son auteur a effectivement fait appel au
                professionnel. Aucun avis n’est rémunéré, et la note affichée ne compte que les avis
                visibles.
              </Paragraph>
            </Section>

            <Section title="Responsabilité">
              <Paragraph>
                Le site est fourni tel quel, gratuitement, et peut être interrompu pour maintenance.
                Les informations de l’annuaire sont renseignées par les professionnels eux-mêmes ou,
                pour une fiche qu’aucun professionnel n’a encore réclamée, par l’équipe du site à
                partir de sources publiques ; elles peuvent alors être incomplètes ou dépassées, et
                le professionnel concerné peut demander à reprendre sa fiche. Les autres contenus
                publiés n’engagent que leurs auteurs.
              </Paragraph>
            </Section>

            <Section title="Vos données">
              <Paragraph>
                Ce qui est collecté, pourquoi, et comment exercer vos droits est décrit dans les{' '}
                <MuiLink component={RouterLink} to="/mentions-legales">
                  mentions légales
                </MuiLink>
                .
              </Paragraph>
            </Section>

            <Section title="Évolution de ces conditions">
              <Paragraph>
                La version en vigueur est celle de cette page, datée en haut.
              </Paragraph>
            </Section>

            <Section title="Droit applicable">
              <Paragraph>
                Ces conditions sont soumises au droit français. En cas de litige, et à défaut de
                résolution amiable, les tribunaux français sont compétents.
              </Paragraph>
            </Section>
          </Stack>
        </Paper>
      </Box>
    </PageLayout>
  );
}

export default TermsOfUse;
